import { uniq } from "lodash";
import { useCallback, useMemo } from "react";
import { AttendanceTypes } from "src/constants";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { useSchoolAdmin } from "./useSchoolAdmin";

type UseSchoolAdminPermissions = {
  schoolFilter(): GQL.school_bool_exp;
  formSchoolRankFilter(): GQL.form_school_rank_bool_exp;
  formFilter(): GQL.form_bool_exp;
  messageAdhocFilter(): GQL.message_adhoc_bool_exp;
  messageAdhocReceiverFilter(): GQL.message_adhoc_form_receiver_bool_exp;
  messageFormStatusFilter(): GQL.message_form_status_bool_exp;
  messageOfferStatusFilter(): GQL.offer_bool_exp;
  messageWaitlistStatusFilter(): GQL.waitlist_bool_exp;
  hasSectionAccess(section: GQL.FormTemplateSectionFragment): boolean;
  hasQuestionAccess(
    question: GQL.FormQuestionWithoutBranchingFragment,
    attendanceContext: AttendanceTypes | string | null
  ): boolean;
};

export function useSchoolAdminPermissions(): UseSchoolAdminPermissions {
  const { isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds } =
    useSchoolAdmin();
  const allSchools = useMemo(
    () => uniq([...applyingSchoolIds, ...attendingSchoolIds]),
    [applyingSchoolIds, attendingSchoolIds]
  );

  const schoolFilter = useCallback(
    () => (isElevatedSchoolAdmin ? createSchoolFilter(allSchools) : {}),
    [isElevatedSchoolAdmin, allSchools]
  );
  const formSchoolRankFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createFormSchoolRankFilter(applyingSchoolIds, attendingSchoolIds)
        : {},
    [isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds]
  );

  const formFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createFormFilter(applyingSchoolIds, attendingSchoolIds)
        : {},
    [isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds]
  );

  const messageAdhocFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createMessageAdhocFilter(attendingSchoolIds, applyingSchoolIds)
        : {},
    [isElevatedSchoolAdmin, attendingSchoolIds, applyingSchoolIds]
  );
  const messageAdhocReceiverFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createMessageAdhocReceiverFilter(
            attendingSchoolIds,
            applyingSchoolIds
          )
        : {},
    [isElevatedSchoolAdmin, attendingSchoolIds, applyingSchoolIds]
  );
  const messageFormStatusFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createMessageFormStatusFilter(attendingSchoolIds, applyingSchoolIds)
        : {},
    [isElevatedSchoolAdmin, attendingSchoolIds, applyingSchoolIds]
  );

  const messageOfferStatusFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createMessageOfferStatusFilter(attendingSchoolIds, applyingSchoolIds)
        : {},
    [isElevatedSchoolAdmin, attendingSchoolIds, applyingSchoolIds]
  );

  const messageWaitlistStatusFilter = useCallback(
    () =>
      isElevatedSchoolAdmin
        ? createMessageWaitlistStatusFilter(
            attendingSchoolIds,
            applyingSchoolIds
          )
        : {},
    [isElevatedSchoolAdmin, attendingSchoolIds, applyingSchoolIds]
  );

  const hasSectionAccess = hasPermissionLevelAccess;
  const hasQuestionAccess = useCallback(
    (
      q: GQL.FormQuestionWithoutBranchingFragment,
      attendanceContext: string | null
    ) =>
      hasPermissionLevelAccess(q) &&
      (attendanceContext === AttendanceTypes.Attending ||
        !q.form_question_schools.length ||
        !!q.form_question_schools.find((fqs) =>
          applyingSchoolIds.includes(fqs.school_id)
        )),
    [applyingSchoolIds]
  );

  return {
    schoolFilter,
    formSchoolRankFilter,
    formFilter,
    messageAdhocFilter,
    messageAdhocReceiverFilter,
    messageFormStatusFilter,
    messageOfferStatusFilter,
    messageWaitlistStatusFilter,
    hasSectionAccess,
    hasQuestionAccess,
  };
}

const hasPermissionLevelAccess = (obj: { permission_level: string | null }) =>
  !obj.permission_level || obj.permission_level === HasuraRole.SCHOOL_ADMIN;

const createSchoolFilter = (allSchools: uuid[]): GQL.school_bool_exp => ({
  id: {
    _in: allSchools,
  },
});

const createFormSchoolRankFilter = (
  applyingSchoolIds: uuid[],
  attendingSchoolIds: uuid[]
): GQL.form_school_rank_bool_exp => ({
  _or: [
    { school_id: { _in: applyingSchoolIds } },
    {
      form: {
        form_attending_school: {
          school_id: { _in: attendingSchoolIds },
        },
      },
    },
  ],
});

const createFormFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.form_bool_exp => {
  const filter: GQL.form_bool_exp["_or"] = [];

  if (applyingSchoolIds.length > 0) {
    filter.push({
      form_school_ranks: { school_id: { _in: applyingSchoolIds } },
    });
    filter.push({ previous_offer: { school_id: { _in: applyingSchoolIds } } });
    filter.push({
      previous_waitlist: { school_id: { _in: applyingSchoolIds } },
    });
  }

  if (attendingSchoolIds.length > 0) {
    filter.push({
      form_attending_school: {
        school_id: { _in: attendingSchoolIds },
      },
    });
  }

  return { _or: filter };
};

const createMessageAdhocFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.message_adhoc_bool_exp => ({
  message_adhoc_form_receivers: {
    ...createMessageAdhocReceiverFilter(attendingSchoolIds, applyingSchoolIds),
  },
});

const createMessageAdhocReceiverFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.message_adhoc_form_receiver_bool_exp => {
  const filter: GQL.form_bool_exp["_or"] = [];

  if (applyingSchoolIds.length > 0) {
    filter.push({
      form_school_ranks: { school_id: { _in: applyingSchoolIds } },
    });
    filter.push({ previous_offer: { school_id: { _in: applyingSchoolIds } } });
    filter.push({
      previous_waitlist: { school_id: { _in: applyingSchoolIds } },
    });
  }

  if (attendingSchoolIds.length > 0) {
    filter.push({
      form_attending_school: {
        school_id: { _in: attendingSchoolIds },
      },
    });
  }

  return {
    form: { _or: filter },
  };
};

const createMessageFormStatusFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.message_form_status_bool_exp => {
  const filter: GQL.form_bool_exp["_or"] = [];

  if (applyingSchoolIds.length > 0) {
    filter.push({
      form_school_ranks: { school_id: { _in: applyingSchoolIds } },
    });
    filter.push({ previous_offer: { school_id: { _in: applyingSchoolIds } } });
    filter.push({
      previous_waitlist: { school_id: { _in: applyingSchoolIds } },
    });
  }

  if (attendingSchoolIds.length > 0) {
    filter.push({
      form_attending_school: {
        school_id: { _in: attendingSchoolIds },
      },
    });
  }

  return { form: { _or: filter } };
};

const createMessageOfferStatusFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.offer_bool_exp => {
  const filter: GQL.form_bool_exp["_or"] = [];

  if (applyingSchoolIds.length > 0) {
    filter.push({
      form_school_ranks: { school_id: { _in: applyingSchoolIds } },
    });
    filter.push({ previous_offer: { school_id: { _in: applyingSchoolIds } } });
    filter.push({
      previous_waitlist: { school_id: { _in: applyingSchoolIds } },
    });
  }

  if (attendingSchoolIds.length > 0) {
    filter.push({
      form_attending_school: {
        school_id: { _in: attendingSchoolIds },
      },
    });
  }

  return { form: { _or: filter } };
};

const createMessageWaitlistStatusFilter = (
  attendingSchoolIds: uuid[],
  applyingSchoolIds: uuid[]
): GQL.waitlist_bool_exp => {
  const filter: GQL.form_bool_exp["_or"] = [];

  if (applyingSchoolIds.length > 0) {
    filter.push({
      form_school_ranks: { school_id: { _in: applyingSchoolIds } },
    });
    filter.push({ previous_offer: { school_id: { _in: applyingSchoolIds } } });
    filter.push({
      previous_waitlist: { school_id: { _in: applyingSchoolIds } },
    });
  }

  if (attendingSchoolIds.length > 0) {
    filter.push({
      form_attending_school: {
        school_id: { _in: attendingSchoolIds },
      },
    });
  }

  return { form: { _or: filter } };
};
